import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  Pagination,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import { authenticatedFetch } from '../../utils/api';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Company {
  id: number;
  name: string;
  address: string;
  phone1: number;
  phone2?: number;
  gst_number: string;
  created_at: string;
}

type SortOption = 'default' | 'name_asc' | 'name_desc';

export default function CompanyList() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('default');
  const navigate = useNavigate();
  const { translations: t } = useTranslation();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchCompanies();
  }, [page, debouncedSearch, sortBy]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
      const sortParam = `&sort=${sortBy}`;
      const response = await authenticatedFetch(`${API_URL}/companies?page=${page}&per_page=25${searchParam}${sortParam}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }

      const data = await response.json();
      setCompanies(data);
      
      // Get pagination info from headers
      const totalPagesHeader = response.headers.get('X-Total-Pages');
      if (totalPagesHeader) {
        setTotalPages(parseInt(totalPagesHeader));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SortOption);
    setPage(1); // Reset to first page when sort changes
  };

  return (
    <Container maxWidth="lg">
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            mb: { xs: 2, sm: 3 }, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              flex: 1,
            }}>
              <TextField
                size="small"
                placeholder={t.companies.searchByName}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: { xs: '100%', sm: '300px' },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                  }
                }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <SortIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    backgroundColor: 'background.paper',
                  }}
                >
                  <MenuItem value="default">{t.companies.sortDefault}</MenuItem>
                  <MenuItem value="name_asc">{t.companies.sortNameAsc}</MenuItem>
                  <MenuItem value="name_desc">{t.companies.sortNameDesc}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => navigate('/companies/new')}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
            >
              {t.companies.createNew}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t.companies.name}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t.companies.address}</TableCell>
                  <TableCell>{t.companies.phone}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{t.companies.phone} 2</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t.companies.gst}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t.companies.noCompanies}
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow 
                      key={company.id}
                      onClick={() => navigate(`/companies/${company.id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {company.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
                          GST: {company.gst_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{company.address}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{company.phone1}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { lg: 'none' } }}>
                          {company.phone2 && `/ ${company.phone2}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{company.phone2 || '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{company.gst_number}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}